import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from 'react-router-dom'
import { isSupabaseConfigured } from './lib/supabase'
import { ToastProvider } from './components/shared/Toast'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { CareProvider, useCare } from './contexts/CareContext'
import LoadingSpinner from './components/shared/LoadingSpinner'
import AppShell from './components/layout/AppShell'

import AuthPage from './pages/AuthPage'
import OnboardingPage from './pages/OnboardingPage'
import DashboardPage from './pages/DashboardPage'
import GoalsPage from './pages/GoalsPage'
import TeamPage from './pages/TeamPage'
import TrackingPage from './pages/TrackingPage'
import MorePage from './pages/MorePage'
import ProfilePage from './pages/ProfilePage'
import CareLogPage from './pages/CareLogPage'
import MedicationsPage from './pages/MedicationsPage'
import AppointmentsPage from './pages/AppointmentsPage'
import CarePlanActionsPage from './pages/CarePlanActionsPage'
import GiniePage from './pages/GiniePage'
import AdminDashboardPage from './pages/AdminDashboardPage'
import CaregiverHomePage from './pages/CaregiverHomePage'
import TasksPage from './pages/TasksPage'
import DocumentsPage from './pages/DocumentsPage'
import ProgressPage from './pages/ProgressPage'
import ReportsPage from './pages/ReportsPage'
import InvitePage from './pages/InvitePage'

/* Friendly screen when .env.local isn't filled in yet. */
function ConfigGate() {
  return (
    <div className="screen">
      <div
        className="container center"
        style={{
          minHeight: '100dvh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: 12,
        }}
      >
        <div style={{ fontSize: 40 }}>🔌</div>
        <h1 className="title">Almost there</h1>
        <p className="muted">
          Mapa Care isn't connected to Supabase yet. Add your project's URL and
          anon key to <code>.env.local</code>, then restart the dev server.
        </p>
        <p className="hint">
          See <code>SETUP.md</code> for the full checklist.
        </p>
      </div>
    </div>
  )
}

/* Requires an authenticated session. */
function RequireAuth({ children }) {
  const { session, loading } = useAuth()
  const location = useLocation()
  if (loading) return <LoadingSpinner full label="Loading your account…" />
  if (!session) return <Navigate to="/auth" replace state={{ from: location }} />
  return children
}

/* Inside auth: send users with no care recipient to onboarding. */
function CareGate({ children }) {
  const { loading, hasRecipients } = useCare()
  if (loading) return <LoadingSpinner full label="Loading care profile…" />
  if (!hasRecipients) return <Navigate to="/onboarding" replace />
  return children
}

/* Caregivers get a shift-focused home instead of the family dashboard. */
function HomeRoute() {
  const { isCaregiver } = useCare()
  return isCaregiver ? <CaregiverHomePage /> : <DashboardPage />
}

/* Already-authed users shouldn't see the auth page. */
function RedirectIfAuthed({ children }) {
  const { session, loading } = useAuth()
  if (loading) return <LoadingSpinner full />
  if (session) return <Navigate to="/" replace />
  return children
}

function AppRoutes() {
  return (
    <Routes>
      <Route
        path="/auth"
        element={
          <RedirectIfAuthed>
            <AuthPage />
          </RedirectIfAuthed>
        }
      />

      <Route path="/invite" element={<InvitePage />} />

      {/* Onboarding: authed, but before the care-recipient gate */}
      <Route
        path="/onboarding"
        element={
          <RequireAuth>
            <OnboardingPage />
          </RequireAuth>
        }
      />

      {/* Main app: authed + has a care recipient */}
      <Route
        element={
          <RequireAuth>
            <CareGate>
              <AppShell />
            </CareGate>
          </RequireAuth>
        }
      >
        <Route index element={<HomeRoute />} />
        <Route path="/goals" element={<GoalsPage />} />
        <Route path="/tracking" element={<TrackingPage />} />
        <Route path="/team" element={<TeamPage />} />
        <Route path="/more" element={<MorePage />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/care-log" element={<CareLogPage />} />
        <Route path="/medications" element={<MedicationsPage />} />
        <Route path="/appointments" element={<AppointmentsPage />} />
        <Route path="/care-plan-actions" element={<CarePlanActionsPage />} />
        <Route path="/ginie" element={<GiniePage />} />
        <Route path="/admin" element={<AdminDashboardPage />} />
        <Route path="/tasks" element={<TasksPage />} />
        <Route path="/documents" element={<DocumentsPage />} />
        <Route path="/progress" element={<ProgressPage />} />
        <Route path="/reports" element={<ReportsPage />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

export default function App() {
  if (!isSupabaseConfigured) return <ConfigGate />

  return (
    <ToastProvider>
      <AuthProvider>
        <CareProvider>
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
        </CareProvider>
      </AuthProvider>
    </ToastProvider>
  )
}
